import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => (
  <div className="bg-black text-white pt-24">
    <div className="bg-gradient-to-br from-black to-gray-900 py-16">
      <div className="container mx-auto px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy <span className="text-[#F4B400]">Policy</span></h1>
        <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>
    </div>
    <div className="bg-white text-black py-16">
      <div className="container mx-auto px-6 max-w-4xl prose prose-lg">
        <h2 className="text-2xl font-bold mt-8 mb-4">1. Introduction</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Enrigo Studio ("we," "our," or "us"), operated through <strong>ealagon.com</strong>, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">2. Information We Collect</h2>
        <h3 className="text-xl font-semibold mt-6 mb-3">Personal Information</h3>
        <p className="text-gray-700 leading-relaxed mb-4">We may collect personal information that you voluntarily provide, including:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>Name and email address (through our contact form)</li>
          <li>Phone number (when you contact us directly)</li>
          <li>Billing and shipping information (for purchases)</li>
          <li>Account credentials (email and password for registered users)</li>
          <li>Any messages or content you submit through our forms</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">Automatically Collected Information</h3>
        <p className="text-gray-700 leading-relaxed mb-4">When you visit our website, we may automatically collect:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>IP address and browser type</li>
          <li>Device information and operating system</li>
          <li>Pages visited, time spent, and navigation patterns</li>
          <li>Referring website or source</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">3. How We Use Your Information</h2>
        <p className="text-gray-700 leading-relaxed mb-4">We use the collected information for:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>Providing and maintaining our services</li>
          <li>Responding to your inquiries and contact form submissions</li>
          <li>Processing transactions and sending related information</li>
          <li>Sending promotional communications (with your consent)</li>
          <li>Improving our website and user experience</li>
          <li>Analyzing website traffic and usage patterns</li>
          <li>Displaying personalized advertisements through Google AdSense</li>
          <li>Compliance with legal obligations</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">4. Third-Party Services</h2>
        <p className="text-gray-700 leading-relaxed mb-4">We use the following third-party services:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li><strong>Google Analytics (GA4):</strong> To analyze website traffic and user behavior. Google Analytics uses cookies to collect anonymous data. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F4B400] hover:underline">Google Privacy Policy</a></li>
          <li><strong>Google Tag Manager:</strong> To manage tracking scripts on our website.</li>
          <li><strong>Google AdSense:</strong> To display advertisements. AdSense may use cookies and web beacons to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising at <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-[#F4B400] hover:underline">Google Ads Settings</a>.</li>
          <li><strong>Cloudinary:</strong> For image hosting and media management.</li>
          <li><strong>MongoDB Atlas:</strong> For secure database storage.</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">5. Cookies and Tracking Technologies</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          We use cookies and similar technologies to enhance your experience. For detailed information about the cookies we use, please see our <Link to="/cookies" className="text-[#F4B400] hover:underline">Cookie Policy</Link>.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">6. Data Sharing and Disclosure</h2>
        <p className="text-gray-700 leading-relaxed mb-4">We do not sell your personal information. We may share data with:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>Service providers who assist in our operations (hosting, analytics, advertising)</li>
          <li>Legal authorities when required by law or to protect our rights</li>
          <li>Business partners with your explicit consent</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">7. Data Security</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          We implement industry-standard security measures including encryption, secure connections (SSL/TLS), and access controls to protect your information. However, no method of transmission over the internet is 100% secure.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">8. Your Rights</h2>
        <p className="text-gray-700 leading-relaxed mb-4">You have the right to:</p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>Access, update, or delete your personal information</li>
          <li>Opt out of marketing communications</li>
          <li>Opt out of personalized advertising</li>
          <li>Request a copy of your data</li>
          <li>Withdraw consent at any time</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">9. Children's Privacy</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">10. Changes to This Policy</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">11. Contact Us</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          If you have any questions about this Privacy Policy, please contact us:
        </p>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
          <li>Email: <a href="mailto:enrigo@ealagon.com" className="text-[#F4B400] hover:underline">enrigo@ealagon.com</a></li>
          <li>Phone: <a href="tel:+18608470649" className="text-[#F4B400] hover:underline">(860) 847-0649</a></li>
          <li>Address: 30 Airport Rd, Hartford, CT 06114</li>
        </ul>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
